import React, { useCallback, useEffect, useMemo } from 'react';
import { IItemForGetShortInfo, IGetDishesShort, IGetDishesShortArgs } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getDishesShortByCustomerId } from 'backend/dishes';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import style from './DishesMultipleSelect.module.scss';

interface IDishesMultipleSelect {
	setDishes: (value: string[]) => void;
	setFetchedDishes: (value: IItemForGetShortInfo[]) => void;
	selectedCustomer: Nullable<string>;
	selectedDishes: string[];
}

const DishesMultipleSelect: React.FC<IDishesMultipleSelect> = ({
	selectedDishes,
	selectedCustomer,
	setDishes,
	setFetchedDishes,
}) => {
	const { response, loading, refetch, responseError } = useFetchData<IGetDishesShort, IGetDishesShortArgs>(
		selectedCustomer ? getDishesShortByCustomerId : null,
		{ customerId: selectedCustomer || '', isCombo: true },
	);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = selectedDishes.filter((el) => el !== id);
			setDishes(updatedSelectedItems);
		},
		[selectedDishes, setDishes],
	);

	const dishesList = useMemo(() => {
		return selectedDishes.map((el) => {
			const dish = response?.data.dishes.find((item) => item.id === el);

			return (
				<SelectedItem
					key={el}
					label={dish?.name || ''}
					onDelete={() => handleRemove(el)}
					additionalContent={
						<>
							{dish?.externalId && (
								<ChunkOfTextWithCopying text={dish.externalId} className={style.externalId} />
							)}
						</>
					}
				/>
			);
		});
	}, [selectedDishes, response, handleRemove]);

	useEffect(() => {
		refetch();
	}, [selectedCustomer]);

	useEffect(() => {
		if (response) {
			setFetchedDishes(response.data.dishes);
		}
	}, [response]);

	return (
		<div className={style.container} data-test="DishesMultipleSelect">
			<div className={style.field}>
				<MultipleSelect
					data={response?.data.dishes || []}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					selectedDishes={selectedDishes}
					selectedCustomer={selectedCustomer}
					setDishes={setDishes}
				/>
			</div>
			<div className={style.groupsList}>{dishesList}</div>
		</div>
	);
};

export default React.memo(DishesMultipleSelect);
